// pages/CompetencyQuiz/SettingsTab.js
//
// Quiz settings (competencyQuiz.manage: IT Admin / IT Team): questions per attempt,
// time limit (blank = none), score bands, open / closed. Changes apply to attempts
// started afterwards; an attempt already in progress keeps its questions and timer.
import React, { useEffect, useState } from "react";
import { Alert, Button, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import { toast } from "react-toastify";
import { EmptyState } from "../../Components/Common/DashboardKit";
import { errorText, getSettings, pick, saveSettings } from "../../services/competencyQuizApi";
import useSingleFlight from "./useSingleFlight";

const fromApi = (s) => ({
    title:               pick(s, "Title", "title") || "",
    description:         pick(s, "Description", "description") || "",
    questionsPerAttempt: pick(s, "QuestionsPerAttempt", "questionsPerAttempt") ?? 20,
    timeLimitMinutes:    pick(s, "TimeLimitMinutes", "timeLimitMinutes") ?? "",
    strongPercent:       pick(s, "StrongPercent", "strongPercent") ?? 85,
    reviewPercent:       pick(s, "ReviewPercent", "reviewPercent") ?? 70,
    isActive:            pick(s, "IsActive", "isActive") !== false,
    activeQuestionCount: pick(s, "ActiveQuestionCount", "activeQuestionCount") ?? 0,
    activeDomainCount:   pick(s, "ActiveDomainCount", "activeDomainCount") ?? 0,
});

export default function SettingsTab() {
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const once = useSingleFlight();

    useEffect(() => {
        (async () => {
            try {
                setForm(fromApi(await getSettings()));
            } catch (err) {
                setError(errorText(err, "Could not load the settings."));
            } finally {
                setLoading(false);
            }
        })();
    }, []);

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    const whole = (v) => /^\d+$/.test(String(v ?? "").trim());
    const save = () => once("save", async () => {
        if (!whole(form.questionsPerAttempt)) { toast.error("Questions per attempt must be a whole number."); return; }
        if (String(form.timeLimitMinutes ?? "").trim() !== "" && !whole(form.timeLimitMinutes)) { toast.error("Time limit must be a whole number of minutes, or blank for no limit."); return; }
        if (!whole(form.strongPercent) || !whole(form.reviewPercent)) { toast.error("Enter both score bands as whole percentages."); return; }
        setSaving(true);
        try {
            setForm(fromApi(await saveSettings(form)));
            toast.success("Settings saved. They apply to quizzes started from now on.");
        } catch (err) {
            toast.error(errorText(err, "The settings were not saved."));
        } finally {
            setSaving(false);
        }
    });

    if (loading) return <div className="text-center py-5"><Spinner size="sm" /> Loading…</div>;
    if (error) return <EmptyState icon="ri-error-warning-line" tone="danger" title="Settings unavailable" message={error} />;

    const tooMany = Number(form.questionsPerAttempt) > Number(form.activeQuestionCount);
    const belowDomains = Number(form.questionsPerAttempt) < Number(form.activeDomainCount);

    return (
        <Row className="g-3" style={{ maxWidth: 900 }}>
            <Col xs={12}>
                <Label className="form-label">Title</Label>
                <Input value={form.title} maxLength={200} onChange={(e) => set({ title: e.target.value })} />
            </Col>
            <Col xs={12}>
                <Label className="form-label">Description <span className="small text-muted">(shown on the Take Quiz tab)</span></Label>
                <Input type="textarea" rows={2} maxLength={1000} value={form.description} onChange={(e) => set({ description: e.target.value })} />
            </Col>
            <Col sm={6} md={3}>
                <Label className="form-label">Questions per attempt</Label>
                <Input type="number" min={1} max={200} value={form.questionsPerAttempt} onChange={(e) => set({ questionsPerAttempt: e.target.value })} />
            </Col>
            <Col sm={6} md={3}>
                <Label className="form-label">Time limit (minutes)</Label>
                <Input type="number" min={1} max={600} placeholder="No limit" value={form.timeLimitMinutes} onChange={(e) => set({ timeLimitMinutes: e.target.value })} />
            </Col>
            <Col sm={6} md={3}>
                <Label className="form-label">"Strong" from (%)</Label>
                <Input type="number" min={0} max={100} value={form.strongPercent} onChange={(e) => set({ strongPercent: e.target.value })} />
            </Col>
            <Col sm={6} md={3}>
                <Label className="form-label">"Review recommended" from (%)</Label>
                <Input type="number" min={0} max={100} value={form.reviewPercent} onChange={(e) => set({ reviewPercent: e.target.value })} />
            </Col>
            <Col xs={12}>
                <div className="small text-muted">Below "Review recommended" the band is "Training need". Bands are an internal interpretation, not a pass / fail rule.</div>
                <div className="small text-muted mt-1">
                    {form.activeDomainCount} domains in the bank. Every attempt includes at least one question from each domain,
                    {" "}so an attempt has at least {form.activeDomainCount} questions.
                </div>
                {belowDomains && (
                    <Alert color="info" className="mt-2 mb-0">
                        With {form.questionsPerAttempt} set here, attempts will have {form.activeDomainCount} questions (one per domain).
                    </Alert>
                )}
                {tooMany && (
                    <Alert color="warning" className="mt-2 mb-0">
                        Only {form.activeQuestionCount} questions are active, so an attempt will have {form.activeQuestionCount}.
                    </Alert>
                )}
            </Col>
            <Col xs={12}>
                <FormGroup switch>
                    <Input type="switch" checked={form.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
                    <Label check>Quiz is open (assigned people can start it)</Label>
                </FormGroup>
            </Col>
            <Col xs={12}>
                <Button color="primary" onClick={save} disabled={saving}>
                    {saving ? <Spinner size="sm" className="me-1" /> : <i className="ri-save-line me-1" />}Save settings
                </Button>
            </Col>
        </Row>
    );
}
